import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Button,
  Divider,
  Tooltip,
  Card,
  CardContent,
  Alert,
  AlertTitle
} from '@mui/material';

import { styled } from '@mui/system';
import axiosInstance from '../../utils/axios';

// Optional: example background for the hero
// Replace with your own image or gradient
const HeroSection = styled(Box)({
  background: `linear-gradient(135deg, #1a73e8 0%, #4285f4 100%)`,
  color: '#fff',
  padding: '4rem 1rem',
  textAlign: 'center',
  borderRadius: '8px',
});

export default function AwesomeRegistrationForm() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // State for form fields
  const [practiceName, setPracticeName] = useState('');
  const [practiceWebsite, setPracticeWebsite] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [pims, setPims] = useState('');
  const [zoetisRepName, setZoetisRepName] = useState('');
  const [registrantType, setRegistrantType] = useState('Myself');

  // "Which brand of the following do you utilize?"
  const [parasitePreventivesK9, setParasitePreventivesK9] = useState('');
  const [parasitePreventivesFel, setParasitePreventivesFel] = useState('');
  const [vaccines, setVaccines] = useState('');
  const [diagnosticLabInHouse, setDiagnosticLabInHouse] = useState('');
  const [diagnosticLabSendOut, setDiagnosticLabSendOut] = useState('');

  // Validation function
  const validateForm = () => {
    const errors = {};
    
    if (!practiceName.trim()) {
      errors.practiceName = 'Practice name is required';
    }
    
    if (!contactName.trim()) {
      errors.contactName = 'Contact name is required';
    }
    
    if (!contactEmail.trim()) {
      errors.contactEmail = 'Contact email is required';
    } else if (!/\S+@\S+\.\S+/.test(contactEmail)) {
      errors.contactEmail = 'Please enter a valid email address';
    }
    
    if (!contactPhone.trim()) {
      errors.contactPhone = 'Contact phone is required';
    }
    
    return errors;
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setErrorMessage('');
    setFieldErrors({});
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setErrorMessage('Please fix the errors below before submitting.');
      return;
    }

    setIsSubmitting(true);
    
    const formData = {
      practiceName,
      practiceWebsite,
      contactName,
      contactEmail,
      contactPhone,
      pims,
      zoetisRepName,
      registrantType,
      parasitePreventivesK9,
      parasitePreventivesFel,
      vaccines,
      diagnosticLabInHouse,
      diagnosticLabSendOut,
    };
  
    try {
      const response = await axiosInstance.post('/campaign/2025/new-register-submission', formData);
      console.log('Form submitted successfully:', response.data);
      setFormSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Handle different types of errors
      let errorMsg = 'An error occurred while submitting the form.';
      
      if (error.response) {
        // Server responded with error status
        if (error.response.data && error.response.data.message) {
          errorMsg = error.response.data.message;
        } else if (error.response.status === 400) {
          errorMsg = 'Please check your form data and try again.';
        } else if (error.response.status === 500) {
          errorMsg = 'Server error. Please try again later.';
        } else {
          errorMsg = `Error ${error.response.status}: ${error.response.statusText}`;
        }
      } else if (error.request) {
        // Network error
        errorMsg = 'Network error. Please check your internet connection and try again.';
      }
      
      setErrorMessage(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to check if a field has an error
  const hasError = (fieldName) => {
    return fieldErrors[fieldName] ? true : false;
  };

  // Helper function to get error message for a field
  const getFieldError = (fieldName) => {
    return fieldErrors[fieldName] || '';
  };
  
  // "Thank You" screen
  if (formSubmitted) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
          Thank you for registering!
        </Typography>
        <Typography sx={{ mb: 3 }}>
          The next step is to schedule a brief but necessary meeting with one of our
          specialists to finalize the details. You can do so by clicking the button
          below to schedule a time that is convenient for you.
        </Typography>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => window.open('https://calendly.com/taram-verticalvet/custom-campaign-onboarding', '_blank')}
          >
            Schedule NOW!
          </Button>
        </Box>

        <Typography sx={{ mt: 3 }}>
          If you are unable to find a time that fits your schedule, please email us at{' '}
          <a href="mailto:clientservices@verticalvet.com">
            clientservices@verticalvet.com
          </a>{' '}
          with your need and we will be in touch!
        </Typography>
      </Container>
    );
  }

  // Main form
  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', py: 4 }}>
      {/* Hero Section */}
      <Container maxWidth="md" sx={{ mb: 4 }}>
        <HeroSection>
          <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
            Client Communication Campaign Registration
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
            Please fill out the form below to register for a campaign.{' '}
            <strong>
              Once you click Submit, you'll schedule a campaign onboard meeting.
            </strong>{' '}
            This brief meeting ensures we finalize all details and customize your
            campaign effectively.
          </Typography>
        </HeroSection>
      </Container>

      {/* Form Container */}
      <Container maxWidth="md">
        {/* Error Alert */}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Error</AlertTitle>
            {errorMessage}
          </Alert>
        )}

        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              Practice Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Tooltip title="Enter the official name of your practice/clinic">
                  <TextField
                    label="Practice Name"
                    placeholder="e.g., Otterson Valley Animal Clinic"
                    fullWidth
                    value={practiceName}
                    onChange={(e) => setPracticeName(e.target.value)}
                    error={hasError('practiceName')}
                    helperText={getFieldError('practiceName')}
                    required
                  />
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Practice Website"
                  placeholder="e.g., https://OttersonValleyVet.com"
                  fullWidth
                  value={practiceWebsite}
                  onChange={(e) => setPracticeWebsite(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Name"
                  placeholder="Primary contact or manager"
                  fullWidth
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  error={hasError('contactName')}
                  helperText={getFieldError('contactName')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Email"
                  placeholder="Your best email for communication"
                  fullWidth
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  error={hasError('contactEmail')}
                  helperText={getFieldError('contactEmail')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Phone"
                  placeholder="(555) 555-5555"
                  fullWidth
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  error={hasError('contactPhone')}
                  helperText={getFieldError('contactPhone')}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="PIMS"
                  placeholder="Practice management system"
                  fullWidth
                  value={pims}
                  onChange={(e) => setPims(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Zoetis Reps Name"
                  placeholder="If applicable"
                  fullWidth
                  value={zoetisRepName}
                  onChange={(e) => setZoetisRepName(e.target.value)}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <FormControl>
                <FormLabel>Who is registering?</FormLabel>
                <RadioGroup
                  row
                  value={registrantType}
                  onChange={(e) => setRegistrantType(e.target.value)}
                >
                  <FormControlLabel value="Myself" control={<Radio />} label="Myself" />
                  <FormControlLabel
                    value="Zoetis Rep"
                    control={<Radio />}
                    label="Zoetis Rep"
                  />
                  <FormControlLabel value="VV Team" control={<Radio />} label="VV Team" />
                </RadioGroup>
              </FormControl>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
              Brands You Utilize
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Let us know which brands you currently work with (parasite preventives,
              vaccines, etc.), so we can tailor your campaign.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Parasite Preventives: K9"
                  placeholder="e.g., Simparica Trio, NexGard, etc."
                  fullWidth
                  value={parasitePreventivesK9}
                  onChange={(e) => setParasitePreventivesK9(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Parasite Preventives: Fel"
                  placeholder="e.g., Revolution, Advantage, etc."
                  fullWidth
                  value={parasitePreventivesFel}
                  onChange={(e) => setParasitePreventivesFel(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Vaccines"
                  placeholder="e.g., brand(s) used for core or lifestyle vaccines"
                  fullWidth
                  value={vaccines}
                  onChange={(e) => setVaccines(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Diagnostic Lab:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="In-house"
                      placeholder="e.g., brand or equipment name"
                      fullWidth
                      value={diagnosticLabInHouse}
                      onChange={(e) => setDiagnosticLabInHouse(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Send out"
                      placeholder="e.g., Antech, Idexx, etc."
                      fullWidth
                      value={diagnosticLabSendOut}
                      onChange={(e) => setDiagnosticLabSendOut(e.target.value)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* SUBMIT BUTTON */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="contained"
                size="large"
                sx={{ px: 5, py: 1.5 }}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
